import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import React, { useMemo, useState } from 'react';

const GlobalTableGenerator = ({
  columns,
  rows,
  TableContainerStyle,
  TableStyle,
  tableLayoutFixed = false,
  TableHeadStyle,
  TableBodyStyle,
  permenantLastRow = {},
  loading,
  elevation,
  pagination = false,
  searchOption = false,
  searchBy = [],
}) => {
  const [search, setSearch] = useState('');
  const [paginationData, setPaginationData] = useState({
    page: 0,
    limit: 10,
  });

  const TableRows = useMemo(() => {
    return [
      ...(Boolean(search)
        ? rows?.filter((v) => {
            for (let i of searchBy) {
              if (v?.[i]?.toLowerCase()?.includes(search?.toLowerCase())) return true;
            }
            return false;
          })
        : pagination
          ? rows?.slice(
              paginationData?.limit * (paginationData?.page + 1) - paginationData?.limit,
              paginationData?.limit * (paginationData?.page + 1),
            )
          : rows),
      ...(Object.keys(permenantLastRow).length ? [permenantLastRow] : []),
    ];
  }, [paginationData, rows, pagination, search, searchBy, permenantLastRow]);

  return (
    <TableContainer
      component={Paper}
      elevation={elevation ?? 0}
      aria-label="simple table"
      sx={
        TableContainerStyle ?? {
          border: '1px solid #f3f3f3',
          borderRadius: 1,
        }
      }
    >
      {searchOption && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end', // right align
            p: 2,
          }}
        >
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              sx: { borderRadius: 5 },
              endAdornment: (
                <Stack direction="row">
                  {Boolean(search) && (
                    <IconButton onClick={(e) => setSearch('')}>
                      <ClearOutlinedIcon />
                    </IconButton>
                  )}

                  <IconButton>
                    <SearchOutlinedIcon />
                  </IconButton>
                </Stack>
              ),
            }}
          />
        </Box>
      )}
      <Table sx={{ tableLayout: `${tableLayoutFixed ? 'fixed' : 'auto'}`, ...(TableStyle ?? {}) }}>
        <TableHead sx={TableHeadStyle ?? {}}>
          <TableRow>
            {columns.map(
              ({ headerName, headerAlign, headerRowSpan, headerColSpan, width, flex, minWidth, maxWidth }, i) => {
                return (
                  <TableCell
                    align={headerAlign ?? 'center'}
                    key={`${i}-head`}
                    rowSpan={headerRowSpan ?? 1}
                    colSpan={headerColSpan ?? 1}
                    sx={{
                      ...(width && { minWidth: minWidth ?? width, width, maxWidth: maxWidth ?? width }),
                      overflow: 'hidden',
                      flex,
                    }}
                  >
                    {headerName}
                  </TableCell>
                );
              },
            )}
          </TableRow>
        </TableHead>
        <TableBody sx={TableBodyStyle ?? {}}>
          {loading ? (
            <TableRow>
              <TableCell align="center" colSpan={columns?.length}>
                {<CircularProgress />}
              </TableCell>
            </TableRow>
          ) : TableRows?.length ? (
            TableRows.map((e, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map(({ field, align, colSpan, rowSpan, renderCell }, i) => (
                  <TableCell align={align ?? 'center'} key={`${i}-body`} rowSpan={rowSpan ?? 1} colSpan={colSpan ?? 1}>
                    {renderCell ? renderCell({ value: e?.[field], row: e }) : e?.[field]}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell align="center" colSpan={columns?.length} sx={{ py: 3, color: 'gray', fontWeight: 600 }}>
                No Data
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {pagination && (
        <TablePagination
          component={'div'}
          count={rows?.length ?? 0}
          page={paginationData.page}
          rowsPerPage={paginationData.limit}
          onPageChange={(_, page) => {
            setPaginationData((prev) => ({ ...prev, page }));
          }}
          onRowsPerPageChange={(e) => {
            setPaginationData((prev) => ({ ...prev, limit: e.target.value }));
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      )}
    </TableContainer>
  );
};
export default React.memo(GlobalTableGenerator);
